import { useQuery } from '@tanstack/react-query';

import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useAutoCompleteQuery } from '@/shared/hooks/queries';

import type { EntitySelectDescriptor } from '../field-registry';
import { MultiSelectCombobox } from './multi-select-combobox';
import { SingleSelectCombobox } from './single-select-combobox';
import type { AriaProps, FieldInputValue, Option } from './types';
import { asStringArray } from './value-helpers';

type EntitySelectInputProps = AriaProps & {
  descriptor: EntitySelectDescriptor;
  value: FieldInputValue;
  onChange: (value: FieldInputValue) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  className?: string;
};

const entityOptions = (items: unknown[], descriptor: EntitySelectDescriptor): Option[] => {
  const labelKey = descriptor.labelKey ?? descriptor.valueKey;
  return items.map((item) => {
    const record = item as Record<string, unknown>;
    return {
      value: String(record[descriptor.valueKey]),
      label: String(record[labelKey]),
    };
  });
};

/** Single/multi select backed by a gateway query. */
export function EntitySelectInput(props: EntitySelectInputProps) {
  return props.descriptor.preloadAll ? (
    <PreloadedEntitySelectInput {...props} />
  ) : (
    <AutocompleteEntitySelectInput {...props} />
  );
}

function PreloadedEntitySelectInput({
  descriptor,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  id,
  className,
  ...aria
}: EntitySelectInputProps) {
  const gateways = useGateways();
  const { data: items = [], isLoading } = useQuery({
    queryKey: [descriptor.parentKey],
    queryFn: ({ signal }) =>
      fetchAllPages((input) => descriptor.query(gateways, input, { signal })),
  });

  const options = entityOptions(items, descriptor);
  const emptyText = isLoading ? 'Loading…' : 'No matches';

  if (descriptor.multi) {
    return (
      <MultiSelectCombobox
        {...aria}
        id={id}
        options={options}
        value={asStringArray(value)}
        disabled={disabled}
        placeholder={placeholder}
        emptyText={emptyText}
        className={className}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }

  return (
    <SingleSelectCombobox
      {...aria}
      id={id}
      options={options}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      emptyText={emptyText}
      className={className}
      onChange={onChange}
      onBlur={onBlur}
    />
  );
}

function AutocompleteEntitySelectInput({
  descriptor,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  id,
  className,
  ...aria
}: EntitySelectInputProps) {
  const gateways = useGateways();
  const {
    options: items,
    onSearch,
    isLoading,
  } = useAutoCompleteQuery<unknown>({
    parentKey: descriptor.parentKey,
    query: (input, options) => descriptor.query(gateways, input, options),
    enabled: true,
  });

  const options = entityOptions(items, descriptor);
  const emptyText = isLoading ? 'Loading…' : 'No matches';

  if (descriptor.multi) {
    return (
      <MultiSelectCombobox
        {...aria}
        id={id}
        options={options}
        value={asStringArray(value)}
        disabled={disabled}
        placeholder={placeholder}
        emptyText={emptyText}
        className={className}
        onChange={onChange}
        onSearch={onSearch}
        onBlur={onBlur}
      />
    );
  }

  return (
    <SingleSelectCombobox
      {...aria}
      id={id}
      options={options}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      emptyText={emptyText}
      className={className}
      onChange={onChange}
      onSearch={onSearch}
      onBlur={onBlur}
    />
  );
}
